
import { useState, useEffect } from "react";
import { Membership } from "../../utils/types";

export type SortMembersConfig = {
  key: keyof Membership;
  direction: 'asc' | 'desc';
}

export const useSortMembers = (data: Membership[]) => {
  const [sortConfig, setSortConfig] = useState<SortMembersConfig | null>(null);
  const [sortedData, setSortedData] = useState<Membership[]>(data);

  useEffect(() => {
    if (sortConfig) {
      applySorting(data);
    } else {
      setSortedData(data);
    }
  }, [data, sortConfig]);

  const applySorting = (dataToSort: Membership[]) => {
    if (!sortConfig) return dataToSort;

    const sortedResults = [...dataToSort].sort((a, b) => {
      // Tri selon la configuration
      const { key, direction } = sortConfig;
      
      // Gestion des valeurs null/undefined
      if (a[key] === null || a[key] === undefined) return direction === 'asc' ? 1 : -1;
      if (b[key] === null || b[key] === undefined) return direction === 'asc' ? -1 : 1;
      
      // Trier selon le type de donnée
      if (typeof a[key] === 'string' && typeof b[key] === 'string') {
        return direction === 'asc' 
          ? (a[key] as string).localeCompare(b[key] as string) 
          : (b[key] as string).localeCompare(a[key] as string);
      }
      
      if (typeof a[key] === 'number' && typeof b[key] === 'number') {
        return direction === 'asc' 
          ? (a[key] as number) - (b[key] as number) 
          : (b[key] as number) - (a[key] as number);
      }
      
      if (typeof a[key] === 'boolean' && typeof b[key] === 'boolean') {
        return direction === 'asc' 
          ? (a[key] === b[key] ? 0 : a[key] ? -1 : 1)
          : (a[key] === b[key] ? 0 : a[key] ? 1 : -1);
      }
      
      // Pour les dates
      if (typeof a[key] === 'string' && typeof b[key] === 'string' && 
          (key.includes('date') || key === 'created_at')) {
        return direction === 'asc' 
          ? new Date(a[key] as string).getTime() - new Date(b[key] as string).getTime()
          : new Date(b[key] as string).getTime() - new Date(a[key] as string).getTime();
      }
      
      return 0;
    });

    setSortedData(sortedResults);
    return sortedResults;
  };

  const sortData = (key: keyof Membership) => {
    let direction: 'asc' | 'desc' = 'asc';
    
    // Si on clique sur la même colonne, on inverse la direction
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    setSortConfig({ key, direction });
  };

  return {
    sortConfig,
    sortedData,
    sortData,
  };
};
